import { ITemplateFieldConfig } from './twilio-setup.interface';

export const TWILIO_STEP1_LABELS = {
  AREA_CODE_LABEL: 'Enter your Preferred Area Code',
  AREA_CODE_PLACEHOLDER: 'Enter 3-digit area code (e.g., 201)',
  NO_RESULTS_TEXT: `Enter an area code above and we'll show you available phone numbers.
Click on any number below to select it for your account.`,
};

export const TWILIO_STEP1_ERRORS = {
  AREA_CODE: {
    required: 'Please enter 3 digit numeric value',
    maxlength: 'Please enter 3 digit numeric value',
  },
};

export const TWILIO_STEP2_LABELS = {
  SECTION_BUSINESS_INFORMATION: 'Business Information',
  SECTION_ADDRESS_INFORMATION: 'Address Information',

  BUSINESS_NAME_LABEL: 'Business Name',
  BUSINESS_NAME_PLACEHOLDER:
    'Enter the exact legal business name. E.g. Growth99 LLC',

  BUSINESS_TYPE_LABEL: 'Business Type',
  BUSINESS_TYPE_PLACEHOLDER: 'Select Business Type',

  REGION_LABEL: 'Region of Operations',
  REGION_PLACEHOLDER: 'Region of Operations',

  WEBSITE_LABEL: 'Website URL',
  WEBSITE_PLACEHOLDER: 'eg: www.company.com',

  TERMS_CONDITION_LABEL: 'Terms & Conditions Page URL',
  TERMS_CONDITION_PLACEHOLDER: 'eg: www.company.com/terms',

  PRIVACY_POLICY_LABEL: 'Privacy Policy Page URL',
  PRIVACY_POLICY_PLACEHOLDER: 'eg: www.company.com/privacy',

  ADDRESS1_LABEL: 'Address Line 1',
  ADDRESS1_PLACEHOLDER: 'Enter the first line of the business address',

  ADDRESS2_LABEL: 'Address Line 2',
  ADDRESS2_PLACEHOLDER:
    'If applicable, enter the second line of the business address',

  COUNTRY_LABEL: 'Country',
  COUNTRY_PLACEHOLDER: 'Select Country',

  STATE_LABEL: 'State/Province',
  STATE_PLACEHOLDER: 'Enter state or province',

  CITY_LABEL: 'City',
  CITY_PLACEHOLDER: 'Enter city',

  ZIP_LABEL: 'Zip/Postal Code',
  ZIP_PLACEHOLDER: 'eg: 12345',
};

export const TWILIO_STEP2_ERRORS = {
  BUSINESS_NAME: {
    required: 'Business Name is required',
    maxlength: 'Business Name cannot exceed 100 characters',
  },
  BUSINESS_TYPE: {
    required: 'Business Type is required',
  },
  REGION: {
    required: 'Source is required',
  },
  WEBSITE: {
    required: 'Website URL is required',
    pattern: 'Please enter a valid website URL',
  },
  TERMS_CONDITION: {
    required: 'Terms and conditions URL is required',
    pattern: 'Please enter a valid website URL',
  },
  PRIVACY_POLICY: {
    required: 'Privacy policy URL is required',
    pattern: 'Please enter a valid website URL',
  },
  ADDRESS1: {
    required: 'Address Line 1 is required',
  },
  COUNTRY: {
    required: 'Country is required',
  },
  STATE: {
    required: 'State/Province is required',
    maxlength: 'State/Province cannot exceed 50 characters',
  },
  CITY: {
    required: 'City is required',
    maxlength: 'City cannot exceed 50 characters',
  },
  ZIP: {
    required: 'Zip/Postal Code is required',
    pattern: 'Zip/Postal Code must contain only numbers',
  },
};

export const TWILIO_STEP3_LABELS = {
  SECTION_TITLE: 'Document Upload',

  REGISTRATION_TYPE_LABEL: 'Business Registration Type',
  REGISTRATION_TYPE_PLACEHOLDER: 'Select Registration Type',

  REGISTRATION_NUMBER_LABEL: 'Registration Number',
  REGISTRATION_NUMBER_PLACEHOLDER: 'For Ex: EIN in case of USA',

  FILE_UPLOAD_LABEL: 'EIN Document File Upload',
  FILE_UPLOAD_HELPER: `Upload the first page of your EIN document where the Registration
Number, Business Name and Address Details are mentioned.`,

  FILE_UPLOAD_DROP_TITLE: 'Drop your file here, or click to browse',
  FILE_UPLOAD_SUPPORT_TEXT:
    'Supports: PDF, JPG, PNG, DOC, DOCX<br />Maximum file size: 10MB',
  FILE_UPLOAD_BUTTON_TEXT: 'Select File',

  FILE_SELECTED_TITLE: 'Selected File',
  FILE_READY_FOR_UPLOAD: 'ready for upload',

  USER_CONSENT_1_LABEL:
    'I declare that the information provided is accurate. I acknowledge that Growth99 will be processing the information provided for the purposes of identity verification, and that Growth99 reserves the right to retain the Business Profile information after account closure in the case of a traceback from a regulatory authority or equivalent.',
};

export const TWILIO_STEP3_ERRORS = {
  REGISTRATION_TYPE: {
    required: 'Business Registration Type is required',
  },
  REGISTRATION_NUMBER: {
    required: 'Registration Number is required',
    pattern: 'Registration Number must contain only numbers',
  },
  DOCUMENT_FILE: 'Document file is required',
};

export const TWILIO_STEP4_LABELS = {
  SECTION_TITLE: 'Contact Information',

  BUSINESS_TITLE_LABEL: 'Title',
  BUSINESS_TITLE_PLACEHOLDER: 'Select Title',

  CONTACT_NAME_LABEL: 'First Name',
  CONTACT_NAME_PLACEHOLDER: 'Enter your first name',

  LAST_NAME_LABEL: 'Last Name',
  LAST_NAME_PLACEHOLDER: 'Enter your last name',

  CONTACT_PHONE_LABEL: 'Contact Phone',
  CONTACT_PHONE_PLACEHOLDER: 'Enter 10-digit phone number',

  CONTACT_EMAIL_LABEL: 'Contact Email',
  CONTACT_EMAIL_PLACEHOLDER: 'Enter contact email address',

  CONTACT_ROLE_LABEL: 'Job Role',
  CONTACT_ROLE_PLACEHOLDER: 'Select Job Role',

  USER_CONSENT_LABEL:
    'I confirm that my nominated authorized representatives agree to be contacted for Verification.',
};

export const TWILIO_STEP4_ERRORS = {
  BUSINESS_TITLE: {
    required: 'Title is required',
  },
  CONTACT_NAME: {
    required: 'First Name is required',
    pattern: 'First Name can only contain letters and spaces',
  },
  LAST_NAME: {
    required: 'Last Name is required',
    pattern: 'Last Name can only contain letters and spaces',
  },
  CONTACT_PHONE: {
    required: 'Contact Phone is required',
    pattern: 'Phone number must contain only numbers',
    minlength: 'Phone number must be exactly 10 digits',
    maxlength: 'Phone number must be exactly 10 digits',
  },
  CONTACT_EMAIL: {
    required: 'Contact Email is required',
    email: 'Please enter a valid email address',
  },
  CONTACT_ROLE: {
    required: 'Job Role is required',
  },
};

export const TWILIO_SETUP_TEMPLATES_LABELS = {
  MODAL_TITLE: 'Setup Templates',
  INSTRUCTIONS_TEXT:
    'Provide default templates for your Terms & Conditions and Privacy Policy. These will be generated for your messaging setup. If you already have dedicated pages for these policies, you can skip this step and provide the URLs directly in the Business Information section.',
  TERMS_CONDITION_TEMPLATE_LABEL: 'Terms & Conditions Template',
  TERMS_CONDITION_TEMPLATE_PLACEHOLDER:
    'Enter your terms & conditions text here...',
  PRIVACY_POLICY_TEMPLATE_LABEL: 'Privacy Policy Template',
  PRIVACY_POLICY_TEMPLATE_PLACEHOLDER: 'Enter your privacy policy text here...',
  SUBMIT_BUTTON: 'Setup Templates',
  SKIP_BUTTON: 'Skip & Continue',
  VIEW_SAMPLE_BUTTON: 'View sample',
  INSERT_SAMPLE_BUTTON: 'Insert sample',
  OVERWRITE_CONFIRM_TITLE: 'Overwrite existing content?',
  OVERWRITE_CONFIRM_DESCRIPTION_HTML:
    "Inserting the sample content will replace what you've already written in this field.<br />This action cannot be undone.",
  OVERWRITE_CONFIRM_BUTTON: 'Insert Sample',
  OVERWRITE_CANCEL_BUTTON: 'Cancel',
};

export const TWILIO_SETUP_TEMPLATES_SAMPLE_LINKS = {
  TERMS_CONDITION_SAMPLE_URL:
    'https://docs.google.com/document/d/1vLMQTWkgC-0Ap_sCC56uU6eFH6VZPEYvTYx-E_WjtlA/preview?tab=t.0',
  PRIVACY_POLICY_SAMPLE_URL:
    'https://docs.google.com/document/d/1pT2Dkm7PQ_jug4u7kqAAklyeRJhYZN70NBX4FuAqnw4/preview?tab=t.0',
};

export const TWILIO_SETUP_TEMPLATES_SAMPLE_CONTENT = {
  TERMS_CONDITION_SAMPLE_CONTENT: `<h3>[Business Name] Terms &amp; Conditions</h3>
<p><strong>Effective Date:</strong> [FILL IN: Insert Effective Date]</p>
<h4>1. Agreement to Terms</h4>
<p>By accessing or using the website of [Business Name] ("Company," "we," "us," or "our"), including submitting information through our online forms or communicating with us via text message, you agree to be bound by these Terms &amp; Conditions and our Privacy Policy (available at [FILL IN: Insert Privacy Policy URL — e.g. https://yourdomain.com/privacy]). By using our website, you also acknowledge receipt of our Privacy Policy.</p>
<p>If you do not agree, please do not use our website or services.</p>
<h4>2. Services</h4>
<p>We reserve the right to modify, suspend, or discontinue any part of our services at any time. Where practicable, we will endeavor to provide reasonable advance notice of material changes to our services.</p>
<h4>3. Eligibility</h4>
<p>You must be at least 18 years old to use our website or submit personal information. By using our website, you represent that you meet this requirement. If you have reason to believe a minor has submitted personal information through our website, please contact us immediately at [FILL IN: Email Address] so we may delete that information.</p>
<h4>4. User Responsibilities</h4>
<p>When using our website or services, you agree to:</p>
<ul>
<li>Provide accurate and complete information</li>
<li>Use our website only for lawful purposes</li>
<li>Not misuse, disrupt, or interfere with our website or systems</li>
<li>Not attempt unauthorized access to any part of our systems</li>
</ul>
<h4>5. SMS Messaging Terms</h4>
<p>By providing your mobile phone number through our website forms, scheduling tools, chat features, or other communication channels, you expressly consent to receive text messages from [Business Name]. This consent is obtained at the point of data collection through a clear and conspicuous opt-in disclosure as required by the Telephone Consumer Protection Act (TCPA) and applicable FCC regulations.</p>
<p>These messages may include:</p>
<ul>
<li>Appointment reminders</li>
<li>Service updates</li>
<li>Order confirmations</li>
<li>Customer service responses</li>
<li>Promotional or marketing messages (if applicable)</li>
</ul>
<p><strong>Message Frequency</strong></p>
<p>Message frequency may vary depending on your interaction with us.</p>
<p><strong>Message &amp; Data Rates</strong></p>
<p>Message and data rates may apply according to your mobile carrier plan.</p>
<p><strong>Opt-Out Instructions</strong></p>
<p>You may opt out of receiving text messages at any time by replying:</p>
<ul>
<li>STOP – to unsubscribe</li>
<li>CANCEL or UNSUBSCRIBE – also accepted to unsubscribe</li>
<li>HELP – for assistance</li>
</ul>
<p>Opt-out requests will be processed within 10 business days.</p>
<p>You may also contact us at [FILL IN: Email Address] for support.</p>
<p><strong>Consent Not Required for Purchase</strong></p>
<p>Your consent to receive text messages is not a condition of purchasing any goods or services.</p>
<p><strong>Carriers</strong></p>
<p>Mobile carriers are not liable for delayed or undelivered messages.</p>
<h4>6. Privacy</h4>
<p>We respect your privacy. Any personal information collected through our website or SMS communications is handled in accordance with our Privacy Policy (available at [FILL IN: Insert Privacy Policy URL — e.g. https://yourdomain.com/privacy]).</p>
<p>[We do not sell or share your phone number with third parties for their marketing purposes.]</p>
<p>Personal information collected through our website and SMS channels is retained only as long as necessary to fulfill the purposes for which it was collected, or as required by applicable law. In the event of a data breach affecting your personal information, we will notify you as required by applicable law.</p>
<h4>7. Payments &amp; Billing (If Applicable)</h4>
<p>If you purchase services or products from us:</p>
<ul>
<li>All prices are subject to change without notice.</li>
<li>Payment is due at the time of purchase unless otherwise agreed.</li>
</ul>
<h4>8. Intellectual Property</h4>
<p>All website content, including text, graphics, logos, images, and design elements, is the property of [Business Name], and its suppliers and is protected by applicable intellectual property laws.</p>
<p>You may not reproduce, distribute, or use our content without written permission.</p>
<h4>9. Disclaimer of Warranties</h4>
<p>OUR WEBSITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT GUARANTEE UNINTERRUPTED, SECURE, OR ERROR-FREE OPERATION OF OUR WEBSITE OR SERVICES.</p>
<h4>10. Limitation of Liability</h4>
<p>TO THE FULLEST EXTENT PERMITTED BY LAW, [BUSINESS NAME] SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF, OR INABILITY TO USE, OUR WEBSITE OR SERVICES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</p>
<p>OUR TOTAL CUMULATIVE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATED TO THESE TERMS OR YOUR USE OF OUR SERVICES SHALL NOT EXCEED THE GREATER OF (A) THE TOTAL AMOUNT PAID BY YOU TO [BUSINESS NAME] IN THE SIX (6) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100.00).</p>
<h4>11. Third-Party Links</h4>
<p>Our website may contain links to third-party websites. We are not responsible for the content or practices of those websites.</p>
<h4>12. Dispute Resolution; Arbitration</h4>
<p>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS. Any dispute, claim, or controversy arising out of or relating to these Terms &amp; Conditions or your use of our website or services shall be resolved exclusively by binding individual arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules, except that either party may seek injunctive or other equitable relief in court for infringement of intellectual property rights. YOU AND [BUSINESS NAME] EACH WAIVE THE RIGHT TO A JURY TRIAL AND THE RIGHT TO PARTICIPATE IN A CLASS ACTION OR CLASS ARBITRATION. Arbitration shall take place in [City], [State]. Judgment on the award may be entered in any court of competent jurisdiction.</p>
<h4>13. Termination</h4>
<p>We reserve the right to suspend or terminate access to our website or services at our discretion if these Terms are violated.</p>
<h4>14. Governing Law</h4>
<p>These Terms &amp; Conditions are governed by the laws of the State of [State], without regard to conflict of law principles. Any disputes not subject to the arbitration clause above shall be resolved exclusively in the state or federal courts located in [City], [State], and you consent to the personal jurisdiction of such courts.</p>
<h4>15. Changes to These Terms</h4>
<p>We may update these Terms &amp; Conditions at any time. For material changes, we will provide at least thirty (30) days' prior notice by email (to the address associated with your account, if any) or by posting a prominent notice on our website. Changes will be reflected by updating the "Effective Date" above.</p>
<p>Your continued use of our website after updates constitutes acceptance of the revised terms.</p>
<h4>16. General</h4>
<p><strong>Entire Agreement.</strong> These Terms &amp; Conditions, together with our Privacy Policy and any applicable Order Form or service agreement, constitute the entire agreement between you and [Business Name] regarding your use of our website and services. These terms supersede all prior or contemporaneous communications, proposals, and agreements, whether oral or written, between you and [Business Name] relating to the same subject matter.</p>
<p><strong>Amendments.</strong> Except as provided in Section 15 (Changes to These Terms), these terms may not be amended or modified except by a written agreement signed by both parties or through an account management or online process made available by [Business Name] on its website. No other act, document, usage, or custom will be deemed to amend or modify these terms.</p>
<p><strong>Severability.</strong> If any provision of these terms is found to be unenforceable or invalid by a court of competent jurisdiction, that provision will be enforced to the maximum extent permitted by law, and the remaining provisions will continue in full force and effect.</p>
<p><strong>Waiver.</strong> The failure of [Business Name] to enforce any right or provision of these terms will not constitute a waiver of that right or provision. Any waiver of any provision of these terms will be effective only if in writing and signed by [Business Name].</p>
<p><strong>Assignment.</strong> You may not assign or transfer any of your rights or obligations under these terms without the prior written consent of [Business Name]. [Business Name] may assign these terms, in whole or in part, without restriction.</p>
<h4>17. Contact Information</h4>
<p>If you have any questions regarding these Terms &amp; Conditions, please contact us:</p>
<p>[Business Name]<br />[Street Address]<br />[City, State, ZIP]<br />[Phone Number]<br />[Email Address]</p>`,
  PRIVACY_POLICY_SAMPLE_CONTENT: `<h3>[Client's Business Name] Privacy Policy</h3>
<p><strong>Effective Date:</strong> [Current Date]</p>
<h4>Our Commitment to Your Privacy</h4>
<p>At [Client's Business Name], we are dedicated to respecting and protecting your privacy. This Privacy Policy outlines how we collect, use, and safeguard your personal information when you interact with our website, mobile app, or services.</p>
<h4>Data We Collect</h4>
<p>We collect various types of information:</p>
<ul>
<li>Non-Personally-Identifying Information. This includes details such as browser type, language preference, referring site, and the date and time of each visitor request. This information helps us understand how visitors use our website and improve our services.</li>
<li>Potentially Personally-Identifying Information. For users who log in or leave comments on our website, we may collect Internet Protocol (IP) addresses.</li>
<li>Personally-Identifying Information. When you engage with our services, we may collect personal details such as your name, contact information (email and phone number), and other information relevant to the services you request.</li>
</ul>
<h4>How We Use Your Information</h4>
<p>Your data is used to:</p>
<ul>
<li>Operate and improve our website and services.</li>
<li>Customize your experience with our offerings.</li>
<li>Develop new services and products.</li>
<li>Communicate with you regarding appointments, promotions, and updates.</li>
<li>Process financial transactions.</li>
<li>Send you notifications, with your consent.</li>
<li>Ensure security and prevent fraudulent activities.</li>
</ul>
<h4>Sharing Your Information</h4>
<p>We may share your information with:</p>
<ul>
<li>Third-Party Service Providers. These providers support our operations, such as customer support, payment processing, and technical services. These third parties are bound by confidentiality agreements and are only permitted to use your data for the purposes we specify.</li>
<li>Legal Authorities. We may disclose your information if required by law or if we believe in good faith that it is necessary to protect the rights, property, or safety of [Client's Business Name], our users, or the public.</li>
</ul>
<p>We do not rent or sell your personally-identifying information to third parties for marketing or advertising purposes.</p>
<h4>Protection of Your Data</h4>
<p>We implement a variety of security measures to protect your personal information from unauthorized access, alteration, or destruction. While we strive to use commercially acceptable means to protect your data, please note that no method of transmission over the Internet or electronic storage is 100% secure.</p>
<h4>Your Data Rights</h4>
<p>Depending on your location, you may have the following rights:</p>
<ul>
<li>Access. You can request access to the personal data we hold about you.</li>
<li>Correction. You can request that we correct any inaccuracies in your personal data.</li>
<li>Deletion. You can request that we delete your personal data, subject to certain legal obligations.</li>
<li>Restriction. You can request limitations on how we process your personal data.</li>
</ul>
<p>To exercise any of these rights, please contact us using the information provided below.</p>
<h4>Cookies</h4>
<p>We use cookies to enhance your experience on our website. Cookies help us track your preferences and understand how you interact with our site. If you prefer, you can set your browser to refuse cookies, but this may limit your ability to use certain features of our website.</p>
<h4>Children's Privacy</h4>
<p>We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we knowingly sell such personal information. By using the Services, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor dependent's use of the Services. If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18, please contact us at [Email Address].</p>
<h4>CCPA (doing business in California)</h4>
<p>Information We Collect: We collect the following categories of personal information from California residents, depending on how you interact with our services:</p>
<ul>
<li>Identifiers: Such as your name, email address, IP address, and other contact information.</li>
<li>Commercial Information: Such as records of products or services purchased.</li>
<li>Internet or Other Electronic Network Activity: Such as browsing history, search history, and interactions with our website.</li>
<li>Geolocation Data: Such as physical location from your device when using our website.</li>
<li>Professional or Employment-Related Information: Such as job title and company name.</li>
<li>Inferences: Derived from the information you provide to create a profile or analysis.</li>
</ul>
<h4>SMS Communications</h4>
<p><strong>Use of SMS Communications:</strong> We may use your phone number to send SMS messages related to appointments, service updates, and promotional offers, where you have provided your consent to receive such communications.</p>
<p><strong>Your Choices and Rights:</strong> You may opt out at any time by replying "STOP." For assistance, reply "HELP" or contact us through our website. SMS consent is not a condition of purchase. Mobile numbers will not be shared with third parties for marketing purposes.</p>
<h4>Business Transfers</h4>
<p>In the event that [Client's Business Name] or substantially all of its assets are acquired, or if we go out of business or enter bankruptcy, your information may be transferred to or acquired by a third party. You acknowledge that such transfers may occur, and that any acquirer of [Client's Business Name] may continue to use your personal information as set forth in this policy.</p>
<h4>Policy Updates</h4>
<p>We may update this Privacy Policy from time to time. When changes are made, we will revise the "Effective Date" at the top of this page. We encourage you to review this policy periodically to stay informed about how we are protecting your information.</p>
<h4>Contact Information</h4>
<p>If you have any questions or concerns about our Privacy Policy or how your information is handled, please contact us.</p>
<p>[Client's Business Name]<br />[Street Address]<br />[City, State, ZIP]<br />[Phone Number]<br />[Email Address]</p>`,
};

export const TWILIO_SETUP_TEMPLATES_ERRORS = {
  TERMS_CONDITION_TEMPLATE: {
    required: 'Terms & Conditions template content is required',
  },
  PRIVACY_POLICY_TEMPLATE: {
    required: 'Privacy Policy template content is required',
  },
};

export const TWILIO_ACTIVATE_TWO_WAY_TEXT_LABELS = {
  ACTIVATE_BUTTON: 'Activate Now',
  MODAL_TITLE: 'Activate Two-Way Text',
  INSTRUCTIONS_TEXT:
    'Enter the Twilio details to activate two-way text for this business.',
  TWILIO_NUMBER_LABEL: 'Twilio Number',
  TWILIO_NUMBER_PLACEHOLDER: 'Enter Twilio number',
  TWILIO_NUMBER_PATH_SID_LABEL: 'Twilio Number Path SID',
  TWILIO_NUMBER_PATH_SID_PLACEHOLDER: 'Enter Twilio Number Path SID',
  ACCOUNT_SID_LABEL: 'Account SID',
  ACCOUNT_SID_PLACEHOLDER: 'Enter Account SID',
  SUBMIT_BUTTON: 'Activate',
  CANCEL_BUTTON: 'Cancel',
  ACTIVATE_SUCCESS: 'Two-way text activated successfully',
  ACTIVATE_ERROR: 'Failed to activate two-way text',
};

export const TWILIO_DEACTIVATE_DANGER_ZONE_LABELS = {
  TITLE: 'Deactivate two-way text',
  DESCRIPTION:
    "Incoming and outgoing SMS will stop, and this business's Twilio number and campaign registration will be removed. To use two-way text again, you'll need to complete the setup from the start.",
  BUTTON: 'Deactivate two-way text',
  CANCEL_BUTTON: 'Cancel',
  CONFIRM_BUTTON: 'Yes, deactivate',
  LOADING: 'Deactivating…',
  SUCCESS: 'Two-way text deactivated',
  ERROR: "Couldn't deactivate two-way text. Try again.",
};

export const TWO_WAY_TEXT_HISTORY_LABELS = {
  BUTTON: 'History',
  TITLE: 'Two-way text history',
  CLOSE_ARIA: 'Close two-way text history',
  EVENT_SINGULAR: 'event',
  EVENT_PLURAL: 'events',
  BY: 'by',
  USER_ID: 'User ID:',
  TODAY: 'Today',
  EMPTY: 'No activity yet',
  ERROR: "Couldn't load history.",
  RETRY: 'Try again',
  ACCESS_NOTE: 'Support & Admin only',
  TIMES_IN: 'Times in',
  SETUP_STARTED: 'Setup started',
  ACTIVATED: 'Two-way text activated',
  DEACTIVATED: 'Two-way text deactivated',
};

export const TWILIO_ACTIVATE_TWO_WAY_TEXT_PATTERNS = {
  TWILIO_NUMBER: /^\+[1-9]\d{7,14}$/,
  TWILIO_NUMBER_PATH_SID: /^PN[a-zA-Z0-9]{32}$/,
  ACCOUNT_SID: /^AC[a-zA-Z0-9]{32}$/,
};

export const TWILIO_ACTIVATE_TWO_WAY_TEXT_ERRORS = {
  TWILIO_NUMBER: {
    required: 'Twilio number is required',
    pattern: 'Enter a valid E.164 phone number',
  },
  TWILIO_NUMBER_PATH_SID: {
    required: 'Twilio number path SID is required',
    pattern: 'Must start with "PN" and be 34 characters',
  },
  ACCOUNT_SID: {
    required: 'Account SID is required',
    pattern: 'Must start with "AC" and be 34 characters',
  },
};

export const TWILIO_SETUP_TEMPLATE_FIELDS: ITemplateFieldConfig[] = [
  {
    key: 'termsAndConditionText',
    labelId: 'terms-condition-template-label',
    label: TWILIO_SETUP_TEMPLATES_LABELS.TERMS_CONDITION_TEMPLATE_LABEL,
    error: TWILIO_SETUP_TEMPLATES_ERRORS.TERMS_CONDITION_TEMPLATE,
    sampleUrl: TWILIO_SETUP_TEMPLATES_SAMPLE_LINKS.TERMS_CONDITION_SAMPLE_URL,
    sampleContent:
      TWILIO_SETUP_TEMPLATES_SAMPLE_CONTENT.TERMS_CONDITION_SAMPLE_CONTENT,
  },
  {
    key: 'privacyPolicyText',
    labelId: 'privacy-policy-template-label',
    label: TWILIO_SETUP_TEMPLATES_LABELS.PRIVACY_POLICY_TEMPLATE_LABEL,
    error: TWILIO_SETUP_TEMPLATES_ERRORS.PRIVACY_POLICY_TEMPLATE,
    sampleUrl: TWILIO_SETUP_TEMPLATES_SAMPLE_LINKS.PRIVACY_POLICY_SAMPLE_URL,
    sampleContent:
      TWILIO_SETUP_TEMPLATES_SAMPLE_CONTENT.PRIVACY_POLICY_SAMPLE_CONTENT,
  },
];
